import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RejectedValue, ErrorResponse, MainResponse } from 'shared/interfaces/core-interfaces';
import { AuthHeader } from 'services/authHeader';
import { DropdownEmployeeList, EmployeeBasicInfo, EmployeeModelForList, EmployeeSliceModel, Employee, EmployeeCreateRequest, EmployeeEmploymentRequest, EmploymentPeriodRequest, EmploymentPeriod, EmployeeEmployment } from './employee-interfaces';
import { ApplicantForEmployeeResponse } from '../../../ApplicantTracking/state/applicant-tracking-interfaces';
import { nullDate } from 'shared/utils/common';
import { GetAssignedApplicants } from '../../../ApplicantTracking/state/applicantTrackingSlice';
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from 'config/env';

export const GetActiveTenantEmployees = createAsyncThunk<EmployeeModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetActiveTenantEmployees',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/employee/getAllActiveByTenant', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmployeeModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveTenantEmployees = createAsyncThunk<EmployeeModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetInactiveTenantEmployees',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/employee/getAllInactiveByTenant', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmployeeModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetEmployee = createAsyncThunk<Employee, string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetEmployee',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/get?employeeId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as Employee;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetDropDownListForEmployee = createAsyncThunk<DropdownEmployeeList, void, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetDropDownListForEmployee',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/employee/get-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DropdownEmployeeList;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// Self-service: the logged in user fetches their OWN employee profile (no ViewEmployee permission needed).
export const GetMyEmployeeProfile = createAsyncThunk<Employee, void, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetMyEmployeeProfile',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/get-my-profile', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as Employee;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// Self-service: reference lists needed to render the logged in user's own (read-only) profile.
export const GetMyEmployeeSelectList = createAsyncThunk<DropdownEmployeeList, void, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetMyEmployeeSelectList',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/get-my-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DropdownEmployeeList;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateEmployeeBasicInfo = createAsyncThunk<EmployeeBasicInfo, EmployeeBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/UpdateEmployeeBasicInfo',
    async (employee: EmployeeBasicInfo, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(employee)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as EmployeeBasicInfo;
            dispatch(GetActiveTenantEmployees());
            dispatch(GetDropDownListForEmployee());
            dispatch(GetMyEmployeeSelectList());
            dispatch(showNotification({ message: "The employee is successfully updated.", type: NotificationTypes.Success }));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateEmployee = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/DeactivateEmployee',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/deactivate?employeeId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as MainResponse;
            dispatch(GetActiveTenantEmployees());
            dispatch(GetInactiveTenantEmployees());
            dispatch(showNotification({ message: deactivateResponse.message ?? "The employee is successfully deactivated.", type: NotificationTypes.Success }));
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateEmployee = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/ReactivateEmployee',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/reactivate?employeeId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as MainResponse;
            dispatch(GetActiveTenantEmployees());
            dispatch(GetInactiveTenantEmployees());
            dispatch(showNotification({ message: reactivateResponse.message ?? "The employee is successfully reactivated.", type: NotificationTypes.Success }));
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteEmployee = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/DeleteEmployee',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/delete?employeeId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as MainResponse;
            dispatch(GetInactiveTenantEmployees());
            dispatch(showNotification({ message: deleteResponse.message ?? "The employee is successfully deleted.", type: NotificationTypes.Success }));
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateEmployee = createAsyncThunk<MainResponse, EmployeeCreateRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/CreateEmployee',
    async (employee: EmployeeCreateRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employee/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(employee)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetActiveTenantEmployees());
            dispatch(GetDropDownListForEmployee());
            dispatch(GetMyEmployeeSelectList());
            dispatch(showNotification({ message: createResponse.message ?? "The employee is successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateEmployeeFromApplicant = createAsyncThunk<MainResponse, EmployeeCreateRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/CreateEmployeeFromApplicant',
    async (employee: EmployeeCreateRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/create-employee', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(employee)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetAssignedApplicants());
            dispatch(GetActiveTenantEmployees());
            dispatch(GetDropDownListForEmployee());
            dispatch(GetMyEmployeeSelectList());
            dispatch(showNotification({ message: createResponse.message ?? "The employee is successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#region Employment

export const GetEmploymentsByEmployee = createAsyncThunk<EmployeeEmployment[], string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetEmploymentsByEmployee',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/employments/employee/' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmployeeEmployment[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateEmployment = createAsyncThunk<MainResponse, EmployeeEmploymentRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/CreateEmployment',
    async (employment: EmployeeEmploymentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(employment)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            if (employment.employeeId) {
                dispatch(GetEmploymentsByEmployee(employment.employeeId))
            }
            dispatch(GetActiveTenantEmployees());
            dispatch(showNotification({ message: createResponse.message ?? "The employment is successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateEmployment = createAsyncThunk<MainResponse, EmployeeEmploymentRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/UpdateEmployment',
    async (employment: EmployeeEmploymentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(employment)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as MainResponse;
            if (employment.employeeId) {
                dispatch(GetEmploymentsByEmployee(employment.employeeId))
            }
            dispatch(GetActiveTenantEmployees());
            dispatch(showNotification({ message: updateResponse.message ?? "The employment is successfully updated.", type: NotificationTypes.Success }));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const SetEmploymentPrimary = createAsyncThunk<MainResponse, EmployeeEmploymentRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/SetEmploymentPrimary',
    async (employment: EmployeeEmploymentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(employment)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as MainResponse;
            if (employment.employeeId) {
                dispatch(GetEmploymentsByEmployee(employment.employeeId))
            }
            dispatch(GetActiveTenantEmployees());
            dispatch(showNotification({ message: updateResponse.message ?? "The employment is successfully set as primary.", type: NotificationTypes.Success }));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteEmployment = createAsyncThunk<MainResponse, { id: string, deleteAccess: boolean, employeeId: string }, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/DeleteEmployment',
    async ({ id, deleteAccess, employeeId }: { id: string, deleteAccess: boolean, employeeId: string }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/delete/' + id + "?deleteaccess=" + deleteAccess, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as MainResponse;
            if (employeeId) {
                dispatch(GetEmploymentsByEmployee(employeeId))
            }
            dispatch(GetActiveTenantEmployees());
            dispatch(showNotification({ message: deleteResponse.message ?? "The employment is successfully deleted.", type: NotificationTypes.Success }));
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Employment period

export const GetEmploymentPeriodsByEmployment = createAsyncThunk<EmploymentPeriod[], string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/GetEmploymentPeriodsByEmployment',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/employments/periods/' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmploymentPeriod[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateEmploymentPeriod = createAsyncThunk<MainResponse, EmploymentPeriodRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/CreateEmploymentPeriod',
    async (period: EmploymentPeriodRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/create-period', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(period)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            if (period.employmentId) {
                dispatch(GetEmploymentPeriodsByEmployment(period.employmentId))
            }
            dispatch(showNotification({ message: createResponse.message ?? "The employment period is successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateEmploymentPeriod = createAsyncThunk<MainResponse, EmploymentPeriodRequest, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/UpdateEmploymentPeriod',
    async (period: EmploymentPeriodRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/update-period', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(period)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as MainResponse;
            if (period.employmentId) {
                dispatch(GetEmploymentPeriodsByEmployment(period.employmentId))
            }
            dispatch(showNotification({ message: updateResponse.message ?? "The employment period is successfully updated.", type: NotificationTypes.Success }));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteEmploymentPeriod = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/EmployeeSlice/DeleteEmploymentPeriod',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/employments/delete-period/' + id, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as MainResponse;
            dispatch(showNotification({ message: deleteResponse.message ?? "The employment period is successfully deleted.", type: NotificationTypes.Success }));
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

const initialValue: EmployeeSliceModel = {
    listOfActiveTenantEmployees: [],
    listOfInactiveTenantEmployees: [],
    listOfActiveCorporationEmployees: [],
    dropDownLists: null,
    employee: {
        basicInfo: null,
        employments: []
    },
}

export const EmployeeSlice = createSlice({
    name: 'EmployeeSlice',
    initialState: initialValue,
    reducers: {
        resetEmployeeSlice: (state) => {
            state.listOfActiveTenantEmployees = [];
            state.listOfInactiveTenantEmployees = [];
            state.listOfActiveCorporationEmployees = [];
            state.employee = {
                basicInfo: null,
                employments: []
            };
            state.dropDownLists = null;
        },
        resetEmployee: (state) => {
            state.employee = {
                basicInfo: null,
                employments: []
            };

        },
        setEmployeeFromApplicant(state, action: PayloadAction<ApplicantForEmployeeResponse>) {
            const applicant = action.payload;

            state.employee.basicInfo = {
                id: applicant.id ?? null,
                firstName: applicant.firstName,
                lastName: applicant.lastName,
                prefixTitle: applicant.title || null,
                sufixTitle: applicant.titleAfter || null,
                abbreviation: "",
                genderId: applicant.genderId ?? 0,
                dateOfBirth: applicant.dateOfBirth ?? nullDate,
                placeOfBirth: '',
                nationality1: null,
                nationality2: null,
                // socialSecurityNumber: '',
                streetAndNumber: applicant.address ?? '',
                postalCode: '',
                place: '',
                countryId: applicant.countryId ?? null,
                // positionId: applicant.positionId ?? null,
                privatePhone: null,
                secondPhoneNumber: null,
                privateEmail: applicant.email ?? null,
                website: null,
                // tenantLocationId: applicant.tenantLocationId,
                // departmentId: applicant.departmentId,
                // reportsTo: '',
                // permissionRoleId: '',
                companyMobile: '',
                companyEmail: '',
                maritalStatus: applicant.martialStatus ?? null,
                nameday: null,
                religionId: applicant.religionId ?? 0,
                patronSaintDay: null,
                // isSubventionPossible: null,
                // subventionDescription: null,
                hiringRequestId: applicant.hiringRequestId
            };
        }
    },

    extraReducers(builder) {
        //GetActiveTenantEmployees
        builder.addCase(GetActiveTenantEmployees.fulfilled, (state, action: PayloadAction<EmployeeModelForList[]>) => {
            state.listOfActiveTenantEmployees = action.payload;
        })
        //GetInactiveTenantEmployees
        builder.addCase(GetInactiveTenantEmployees.fulfilled, (state, action: PayloadAction<EmployeeModelForList[]>) => {
            state.listOfInactiveTenantEmployees = action.payload;
        })
        //GetEmployee
        builder.addCase(GetEmployee.fulfilled, (state, action: PayloadAction<Employee>) => {
            state.employee = action.payload;
        })
        //GetDropDownListForEmployee
        builder.addCase(GetDropDownListForEmployee.fulfilled, (state, action: PayloadAction<DropdownEmployeeList>) => {
            state.dropDownLists = action.payload;
            state.dropDownLists.nationalities = action.payload.nationalities.map(item => ({
                ...item,
                label: `${item.label}_C`
            }));
        })
        //GetMyEmployeeProfile
        builder.addCase(GetMyEmployeeProfile.fulfilled, (state, action: PayloadAction<Employee>) => {
            state.employee = action.payload;
        })
        //GetMyEmployeeSelectList
        builder.addCase(GetMyEmployeeSelectList.fulfilled, (state, action: PayloadAction<DropdownEmployeeList>) => {
            state.dropDownLists = action.payload;
            state.dropDownLists.nationalities = action.payload.nationalities.map(item => ({
                ...item,
                label: `${item.label}_C`
            }));
        })

        //#region Employment
        //GetEmploymentsByEmployee
        builder.addCase(GetEmploymentsByEmployee.fulfilled, (state, action) => {
            state.employee = {
                ...state.employee,
                employments: action.payload
            };
        })
        //#endregion

        //#region Employment period cases
        //GetEmploymentPeriodsByEmployment
        builder.addCase(GetEmploymentPeriodsByEmployment.fulfilled, (state, action) => {
            const index = state.employee.employments.findIndex(e => e.id === action.meta.arg);
            if (index !== -1) {
                const copyOfEmployments = [...state.employee.employments];

                const sortedPeriods = [...action.payload].sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

                copyOfEmployments[index] = {
                    ...copyOfEmployments[index],
                    periods: sortedPeriods
                };

                state.employee.employments = copyOfEmployments;
            }
        })
        //#endregion Employment periods
    },
});

export const { resetEmployee, resetEmployeeSlice, setEmployeeFromApplicant } = EmployeeSlice.actions;
export default EmployeeSlice.reducer;
